// The Work-section client TRANSPORT + its injection context (mirrors the house SurfaceApi idiom —
// api-client.ts / SurfaceApiContext.tsx). Reads fetch typed projections; the ONE write is the Execute
// commit (brief §3.5): the body carries ONLY the item id + the staged verdict ticks — the authenticated
// Actor is resolved server-side, the client sets NO approval field (P-GATE, G-008), and the surface
// re-reads the card after every commit so it shows what the owning module recorded (G-182, never
// optimistic). INJECTABLE: surfaces consume `useWorkApi()`; the app provides `httpWorkApi()`; a UI test
// provides the fixture/fake — no real backend network in a UI test.

import { createContext, useContext } from 'react';
import type {
  ExecuteResult,
  StagedVerdictWire,
  WorkBoardFilter,
  WorkBoardProjection,
  WorkCardProjection,
  WorkComposerProjection,
  WorkLandingProjection,
} from './projections.ts';

/** The path prefix the Work-section JSON API is served under (extends the dashboard API prefix). */
const WORK_API_PREFIX = '/dashboard/api/work';

/** The transport surface every Work-section component consumes. */
export interface WorkApi {
  readonly getLanding: () => Promise<WorkLandingProjection>;
  /** Read one board; the optional filter narrows the item slice (counts stay whole-board). */
  readonly getBoard: (
    definitionId: string,
    filter?: WorkBoardFilter,
  ) => Promise<WorkBoardProjection>;
  readonly getCard: (itemId: string) => Promise<WorkCardProjection>;
  /**
   * The ONE authenticated commit (brief §3.5): registers every staged verdict and fires the approved
   * payloads through their owning modules, atomically. Returns the honest per-intention outcomes —
   * including `verdict_conflict` when the round changed underneath (the surface then re-reads the
   * card and re-stages; it never retries blindly).
   */
  readonly execute: (
    itemId: string,
    staged: readonly StagedVerdictWire[],
  ) => Promise<ExecuteResult>;
  /** Read the composer's drafted configuration (the operator console review — brief v2 Composer). */
  readonly getComposer: () => Promise<WorkComposerProjection>;
}

/** GET a typed projection. A non-OK response is surfaced honestly (never a fabricated projection). */
async function getJson<T>(path: string): Promise<T> {
  const response = await fetch(path, {
    method: 'GET',
    headers: { accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) {
    throw new Error(`read failed (${String(response.status)})`);
  }
  return (await response.json()) as T;
}

/** Read a sanitised note off a refusal response without leaking an internal error (G-197). */
async function safeNote(response: Response): Promise<string> {
  try {
    const payload = (await response.json()) as { readonly message?: unknown };
    if (typeof payload.message === 'string') return payload.message;
  } catch {
    // A non-JSON body is never surfaced verbatim (it could carry internals) — a generic note instead.
  }
  return 'That action was not permitted.';
}

/** Build the board path with the filter as query params (only the set fields travel). */
function boardPath(definitionId: string, filter?: WorkBoardFilter): string {
  const params = new URLSearchParams();
  if (filter?.state !== undefined) params.set('state', filter.state);
  if (filter?.needsDecision !== undefined)
    params.set('needsDecision', String(filter.needsDecision));
  if (filter?.tag !== undefined) params.set('tag', filter.tag);
  if (filter?.subjectSearch !== undefined) params.set('subject', filter.subjectSearch);
  const query = params.toString();
  const base = `${WORK_API_PREFIX}/board/${encodeURIComponent(definitionId)}`;
  return query === '' ? base : `${base}?${query}`;
}

/**
 * POST the Execute commit. The body is EXACTLY `{ itemId, staged }` — no actor, no approval field
 * (mass-assignment banned; the API authenticates and the owning module's gate machinery rules). A
 * non-OK response returns an honest refused result: HTTP 409 maps to the verdict-conflict screen.
 */
async function postExecute(
  itemId: string,
  staged: readonly StagedVerdictWire[],
): Promise<ExecuteResult> {
  const response = await fetch(`${WORK_API_PREFIX}/execute`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ itemId, staged }),
  });
  if (!response.ok) {
    return {
      ok: false,
      conflict: response.status === 409,
      outcomes: [],
      note: await safeNote(response),
    };
  }
  return (await response.json()) as ExecuteResult;
}

/** The REAL transport: fetch against the served Work endpoints. Wired by the app at mount. */
export function httpWorkApi(): WorkApi {
  return {
    getLanding: () => getJson<WorkLandingProjection>(`${WORK_API_PREFIX}/landing`),
    getBoard: (definitionId, filter) =>
      getJson<WorkBoardProjection>(boardPath(definitionId, filter)),
    getCard: (itemId) =>
      getJson<WorkCardProjection>(`${WORK_API_PREFIX}/card/${encodeURIComponent(itemId)}`),
    execute: (itemId, staged) => postExecute(itemId, staged),
    getComposer: () => getJson<WorkComposerProjection>(`${WORK_API_PREFIX}/composer`),
  };
}

const WorkApiContext = createContext<WorkApi | null>(null);

/** Provide the Work transport to the surfaces (the app root provides http; a test provides a fake). */
export const WorkApiProvider = WorkApiContext.Provider;

/** Read the Work transport. Throws outside the provider (a wiring bug, surfaced honestly). */
export function useWorkApi(): WorkApi {
  const value = useContext(WorkApiContext);
  if (value === null) {
    throw new Error('useWorkApi must be used within the WorkApiProvider (App root or test)');
  }
  return value;
}
